/**
 * Reusable form fields for User create/edit modals
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import { CreateUserRequest } from '@/services/users/usersService';
import { rbacService } from '@/services/rbac/rbacService';
import type { Role } from '@/types/rbac';
import { useAuth } from '@/store/authStore';
import { BuildingOfficeIcon, KeyIcon, IdentificationIcon, ClockIcon, TruckIcon, ClipboardDocumentListIcon, DevicePhoneMobileIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import DriverScheduleEditor from '@/components/drivers/DriverScheduleEditor';
import VehicleAssignment from '@/components/drivers/VehicleAssignment';
import type { DriverSetupDraft } from '@/types/driverSetupDraft';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import { driverService, AssignmentRow } from '@/services/drivers/driverService';
import api from '@/services/api/client';
import toast from 'react-hot-toast';

import DateInput from '@/components/common/DateInput';

interface Organization {
  id: string;
  name: string;
  depth?: number;
}

interface UserFormFieldsProps {
  formData: CreateUserRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreateUserRequest>>;
  organizations: Organization[];
  isEdit?: boolean;
  driverDbId?: number; // Database ID of the driver (only available when editing existing driver)
  userId?: string; // User ID for sending invites
  onEnableDriver?: () => Promise<number | null>; // Auto-save driver when checkbox is checked
  /** Utkast för schema/fordon vid skapande av användare */
  driverSetupDraft?: DriverSetupDraft;
  onDriverSetupDraftChange?: (draft: DriverSetupDraft) => void;
}

const UserFormFields: React.FC<UserFormFieldsProps> = ({
  formData,
  setFormData,
  organizations: _organizations,
  isEdit = false,
  driverDbId: initialDriverDbId,
  userId,
  onEnableDriver,
  driverSetupDraft,
  onDriverSetupDraftChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [driverDbId, setDriverDbId] = useState<number | undefined>(initialDriverDbId);
  const [assignableRoles, setAssignableRoles] = useState<Role[]>([]);

  useEffect(() => {
    let cancelled = false;
    const orgId = formData.organization_id ? String(formData.organization_id) : undefined;
    if (!orgId) {
      setAssignableRoles([]);
      return () => {
        cancelled = true;
      };
    }
    rbacService
      .getActiveRolesForAssignment(orgId)
      .then((roles) => {
        if (cancelled) return;
        setAssignableRoles(roles);
        setFormData((prev) => {
          if (roles.length === 0) return prev;
          if (roles.some((r) => r.code === prev.role)) return prev;
          return { ...prev, role: roles[0].code };
        });
      })
      .catch(() => {
        if (!cancelled) setAssignableRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.organization_id, setFormData]);

  const roleOptions = useMemo(() => {
    const byCode = new Map(assignableRoles.map((r) => [r.code, r]));
    if (formData.role && !byCode.has(formData.role)) {
      byCode.set(formData.role, {
        id: formData.role,
        organization_id: null,
        code: formData.role,
        name: formData.role,
        is_system_role: true,
        is_active: true,
        created_at: '',
        updated_at: '',
      });
    }
    return Array.from(byCode.values()).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, [assignableRoles, formData.role]);

  // Sync prop to state
  useEffect(() => {
    if (initialDriverDbId) setDriverDbId(initialDriverDbId);
  }, [initialDriverDbId]);
  return (
    <div className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          E-post *
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          required
        />
      </div>

      {/* Name fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('profile.firstName')}
          </label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            Efternamn
          </label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
      </div>

      {/* Organization */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1 flex items-center gap-2">
          <BuildingOfficeIcon className="w-4 h-4" />
          Organisation *
        </label>
        <HierarchicalOrgSelect
          value={String(formData.organization_id || '')}
          onChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              organization_id: value,
              role: prev.organization_id === value ? prev.role : '',
            }))
          }
          placeholder={t('userFormFields.selectOrg')}
          showEmptyOption={false}
        />
      </div>

      {/* Role — hämtas från vald organisations toppnod */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          Roll *
        </label>
        <select
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          disabled={!formData.organization_id || roleOptions.length === 0}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-60"
        >
          {!formData.organization_id ? (
            <option value="">{t('userFormFields.selectOrgForRoles')}</option>
          ) : roleOptions.length === 0 ? (
            <option value="">{t('userFormFields.noRolesForOrg')}</option>
          ) : (
            roleOptions.map((role) => (
              <option key={role.code} value={role.code}>
                {role.name}
              </option>
            ))
          )}
        </select>
        {formData.organization_id && roleOptions.length > 0 && (
          <p className="text-xs text-textTertiary mt-1">{t('userFormFields.rolesFromOrganization')}</p>
        )}
        {!user?.is_global_admin && (
          <p className="text-xs text-textTertiary mt-1">
            {t('userFormFields.globalAdminRolesHint')}
          </p>
        )}
      </div>

      {/* Auth method */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          Autentiseringsmetod
        </label>
        <select
          value={formData.auth_method}
          onChange={(e) => setFormData({ 
            ...formData, 
            auth_method: e.target.value as 'PASSWORD' | 'TWO_FACTOR' | 'SAML2' | 'OIDC',
            password: (e.target.value === 'SAML2' || e.target.value === 'OIDC') ? '' : formData.password
          })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
        >
          <option value="PASSWORD">{t('userFormFields.password')}</option>
          <option value="TWO_FACTOR">{t('userFormFields.twoFactorAuth')}</option>
          <option value="SAML2">SSO (SAML2)</option>
          <option value="OIDC">{t('organizations.oidcAuth')}</option>
        </select>
      </div>

      {/* Password for edit of non-SSO users */}
      {isEdit && formData.auth_method !== 'SAML2' && formData.auth_method !== 'OIDC' && (
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('userFormFields.password')}
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('userFormFields.keepCurrentPassword')}
          />
        </div>
      )}

      {/* App password for edit of SSO users */}
      {isEdit && (formData.auth_method === 'SAML2' || formData.auth_method === 'OIDC') && (
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1 flex items-center gap-2">
            <KeyIcon className="w-4 h-4" />
            {t('copy.ui.appPassword')}
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('userFormFields.mobileLoginOptional')}
          />
          <p className="text-xs text-textTertiary mt-1">
            {t('copy.ui.ssoAppPasswordHint')}
          </p>
        </div>
      )}

      {/* Mobile phone */}
      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">
          Mobilnummer
        </label>
        <input
          type="tel"
          value={formData.MobilePhone}
          onChange={(e) => setFormData({ ...formData, MobilePhone: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
        />
      </div>

      {/* Is driver checkbox */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_driver"
          checked={formData.is_driver}
          onChange={async (e) => {
            const checked = e.target.checked;
            setFormData({
              ...formData,
              is_driver: checked,
              driver_data: checked ? formData.driver_data || {} : formData.driver_data,
            });
            
            if (!checked && !isEdit && onDriverSetupDraftChange) {
              onDriverSetupDraftChange({ schedule: null, vehicleAssignment: null });
            }

            // Auto-spara driver vid redigering av befintlig användare
            if (checked && isEdit && !driverDbId && onEnableDriver) {
              const newDriverId = await onEnableDriver();
              if (newDriverId) {
                setDriverDbId(newDriverId);
              }
            }
          }}
          className="w-4 h-4"
        />
        <label htmlFor="is_driver" className="text-sm font-medium text-textDefault">
          {t('userFormFields.isAlsoDriver')}
        </label>
      </div>

      {/* Driver information with tabs */}
      {formData.is_driver && (
        <DriverTabs
          formData={formData}
          setFormData={setFormData}
          isEdit={isEdit}
          driverDbId={driverDbId}
          userId={userId}
          driverSetupDraft={driverSetupDraft}
          onDriverSetupDraftChange={onDriverSetupDraftChange}
        />
      )}
    </div>
  );
};

/** Driver section with tabs */
const DriverTabs: React.FC<{
  formData: CreateUserRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreateUserRequest>>;
  isEdit: boolean;
  driverDbId?: number;
  userId?: string;
  driverSetupDraft?: DriverSetupDraft;
  onDriverSetupDraftChange?: (draft: DriverSetupDraft) => void;
}> = ({
  formData,
  setFormData,
  isEdit,
  driverDbId,
  userId,
  driverSetupDraft,
  onDriverSetupDraftChange,
}) => {
  const { t } = useTranslation();
  const [sendingWelcomeEmail, setSendingWelcomeEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'schedule' | 'vehicle' | 'history'>('info');
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);

  const loadHistory = useCallback(async () => {
    if (!driverDbId) return;
    try {
      const data = await driverService.getAssignments(driverDbId);
      setAssignments(data);
    } catch {
      // Silently fail
    }
  }, [driverDbId]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  const showDriverExtras = Boolean(driverDbId) || (!isEdit && onDriverSetupDraftChange);
  const isCreateDriverDraft = !isEdit && !driverDbId && Boolean(onDriverSetupDraftChange);

  const handleScheduleDraftChange = useCallback(
    (schedule: NonNullable<DriverSetupDraft['schedule']>) => {
      onDriverSetupDraftChange?.({
        schedule,
        vehicleAssignment: driverSetupDraft?.vehicleAssignment ?? null,
      });
    },
    [onDriverSetupDraftChange, driverSetupDraft?.vehicleAssignment]
  );

  const tabs = [
    { id: 'info' as const, label: t('userFormFields.driverInfo'), icon: IdentificationIcon },
    ...(showDriverExtras
      ? [
          { id: 'schedule' as const, label: t('userFormFields.tabSchedule'), icon: ClockIcon },
          { id: 'vehicle' as const, label: t('userFormFields.tabVehicle'), icon: TruckIcon },
          ...(isEdit && driverDbId
            ? [{ id: 'history' as const, label: t('userFormFields.tabHistory'), icon: ClipboardDocumentListIcon }]
            : []),
        ]
      : []),
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Tab headers */}
      <div className="flex border-b border-border bg-bgLight">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary bg-bgElevated'
                : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {/* Förarinformation */}
        {activeTab === 'info' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('profile.licenseNumber')}
                </label>
                <input
                  type="text"
                  value={formData.driver_data?.license_number || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    driver_data: { ...formData.driver_data, license_number: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('profile.licenseExpiry')}
                </label>
                <DateInput value={formData.driver_data?.license_expiry || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    driver_data: { ...formData.driver_data, license_expiry: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-textDefault mb-1">
                {t('copy.ui.licenseCategoriesExample')}
              </label>
              <input
                type="text"
                value={formData.driver_data?.license_categories || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  driver_data: { ...formData.driver_data, license_categories: e.target.value }
                })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('profile.employeeId')}
                </label>
                <input
                  type="text"
                  value={formData.driver_data?.employee_id || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    driver_data: { ...formData.driver_data, employee_id: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  Avdelning
                </label>
                <input
                  type="text"
                  value={formData.driver_data?.department || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    driver_data: { ...formData.driver_data, department: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-textDefault mb-1">
                {t('profile.costCenter')}
              </label>
              <input
                type="text"
                value={formData.driver_data?.cost_center || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  driver_data: { ...formData.driver_data, cost_center: e.target.value }
                })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
              />
            </div>

            {/* Mobilapp-tillgång */}
            <div className="pt-3 mt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-textDefault cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.mobile_access_enabled || false}
                    onChange={(e) => setFormData({ ...formData, mobile_access_enabled: e.target.checked })}
                    className="w-4 h-4 border border-border rounded text-primary focus:ring-primary"
                  />
                  <DevicePhoneMobileIcon className="w-4 h-4" />
                  {t('copy.ui.mobileAppAccess')}
                </label>

                {isEdit && userId && formData.mobile_access_enabled &&
                  formData.email &&
                  formData.auth_method !== 'SAML2' &&
                  formData.auth_method !== 'OIDC' && (
                  <button
                    type="button"
                    disabled={sendingWelcomeEmail}
                    onClick={async () => {
                      setSendingWelcomeEmail(true);
                      try {
                        await api.post(`/api/users/${userId}/send-app-invite`);
                        toast.success(t('usersManagement.sendWelcomeEmailSentTo', { email: formData.email }));
                      } catch (err: unknown) {
                        const message =
                          err && typeof err === 'object' && 'response' in err
                            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                            : undefined;
                        toast.error(message || t('usersManagement.sendWelcomeEmailError'));
                      } finally {
                        setSendingWelcomeEmail(false);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <EnvelopeIcon className="w-3.5 h-3.5" />
                    {sendingWelcomeEmail
                      ? t('usersManagement.sendWelcomeEmailSending')
                      : t('usersManagement.sendWelcomeEmailButton')}
                  </button>
                )}
              </div>
              <p className="text-xs text-textTertiary mt-1 ml-6">
                {t('copy.ui.mobileAppAccessHint')}
              </p>
            </div>
          </div>
        )}

        {/* Arbetsschema – hålls monterat vid skapande så valet inte nollställs vid flikbyte */}
        {showDriverExtras && (driverDbId || isCreateDriverDraft) && (
          <div className={activeTab === 'schedule' ? '' : 'hidden'}>
            <DriverScheduleEditor
              driverId={driverDbId}
              organizationId={formData.organization_id}
              draftMode={isCreateDriverDraft}
              initialDraft={isCreateDriverDraft ? driverSetupDraft?.schedule : undefined}
              onDraftChange={isCreateDriverDraft ? handleScheduleDraftChange : undefined}
            />
          </div>
        )}

        {activeTab === 'vehicle' && showDriverExtras && (driverDbId || isCreateDriverDraft) && (
          <VehicleAssignment
            driverId={driverDbId}
            organizationId={formData.organization_id}
            draftMode={isCreateDriverDraft}
            onDraftChange={
              isCreateDriverDraft && onDriverSetupDraftChange
                ? (vehicleAssignment) =>
                    onDriverSetupDraftChange({
                      schedule: driverSetupDraft?.schedule ?? null,
                      vehicleAssignment,
                    })
                : undefined
            }
          />
        )}

        {/* Historik */}
        {activeTab === 'history' && isEdit && driverDbId && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-textSecondary mb-2">Fordonstilldelningshistorik</p>
            {assignments.length === 0 ? (
              <p className="text-xs text-textTertiary italic">Ingen historik</p>
            ) : (
              <div className="space-y-1">
                {assignments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 p-2 bg-bgLight/30 rounded text-xs border border-border/50">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${a.end_date ? 'bg-gray-400' : 'bg-green-500'}`} />
                    <span className="font-medium min-w-[60px]">{a.vehicle_registration || `#${a.vehicle_id}`}</span>
                    <span className="text-textTertiary flex-1">
                      {new Date(a.start_date).toLocaleDateString('sv-SE')}
                      {a.end_date && ` — ${new Date(a.end_date).toLocaleDateString('sv-SE')}`}
                      {!a.end_date && ` — ${t('userFormFields.ongoing')}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFormFields;
